import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import StoresDiscoveryPage from '../stores/StoresDiscoveryPage';
import ClubsDiscoveryPage from '../clubs/ClubsDiscoveryPage';
import { MapPinIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const DiscoveryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active tab from URL
  const getActiveTab = (): 'stores' | 'clubs' => {
    if (location.pathname.includes('/discover/clubs')) {
      return 'clubs';
    }
    return 'stores';
  };

  const [activeTab, setActiveTab] = useState<'stores' | 'clubs'>(getActiveTab);

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  // Redirect to stores if just /discover
  useEffect(() => {
    if (location.pathname === '/discover') {
      navigate('/discover/stores', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleTabChange = (tab: 'stores' | 'clubs') => {
    setActiveTab(tab);
    navigate(`/discover/${tab}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange('stores')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stores'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MapPinIcon className="h-5 w-5" />
                <span>{t('stores.title') || 'Stores'}</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('clubs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'clubs'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <UserGroupIcon className="h-5 w-5" />
                <span>{t('clubs.title') || 'Clubs'}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto">
        {activeTab === 'stores' ? <StoresDiscoveryPage /> : <ClubsDiscoveryPage />}
      </div>
    </div>
  );
};

export default DiscoveryPage;
